import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
    FaSync, FaFilter, FaCheck, FaTimes, FaUserAlt,
    FaClock, FaChevronLeft, FaChevronRight, FaSearch, FaCopy
} from 'react-icons/fa';
import { toast } from 'react-toastify'; // Toast bildirimleri için
import './css/DataTable.css';
import axiosInstance from '../utils/axiosInstance';

dayjs.extend(relativeTime);

const DataTable = ({
    title,
    data = [],
    isPendingPage,
    isCompletedPage, // Onaylananlar sayfası kontrolü
    fetchData,
    handleApprove = () => { },
    openRejectModal = () => { }
}) => {
    const [filterVisible, setFilterVisible] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isSystemActive, setIsSystemActive] = useState(true);
    const [loadingStatus, setLoadingStatus] = useState(true);
    const itemsPerPage = 10;


    const fetchSystemStatus = () => {
        axiosInstance.get('/siteSettings')
            .then(response => {
                // Senin sayfandaki "bonusRequestsEnabled" değerini alıyoruz
                setIsSystemActive(response.data.bonusRequestsEnabled);
            })
            .catch(error => {
                console.error("Sistem durumu çekilemedi:", error);
            })
            .finally(() => {
                setLoadingStatus(false);
            });
    };

    // Kopyalama Fonksiyonu
    const copyToClipboard = (text) => {
        if (!text || text === '—') return;
        navigator.clipboard.writeText(text)
            .then(() => toast.success('📋 Kopyalandı!', { autoClose: 1000, hideProgressBar: true }))
            .catch(() => toast.error('Kopyalama başarısız!'));
    };

    // --- OTOMATİK TAKİP (LIFECYCLE) ---
    useEffect(() => {
        fetchSystemStatus(); // İlk açılışta çek

        // Her 20 saniyede bir durumu arka planda sessizce güncelle
        const interval = setInterval(fetchSystemStatus, 20000);

        return () => clearInterval(interval); // Sayfadan çıkınca durdur
    }, []);

    const [bonusNames, setBonusNames] = useState({});

    useEffect(() => {
        const fetchBonusNames = async () => {
            try {
                const response = await axiosInstance.get('/bonus/selection-data');

                // İç içe gelen kategorili veriyi düz bir nesneye çeviriyoruz:
                // { "type1": "İsim 1", "type2": "İsim 2" }
                const nameMap = {};

                response.data.forEach(category => {
                    category.items.forEach(bonus => {
                        nameMap[bonus.type] = bonus.name;
                    });
                });

                setBonusNames(nameMap);
            } catch (err) {
                console.error("Bonus isimleri yüklenemedi:", err);
            }
        };

        fetchBonusNames();
    }, []);

    const filteredData = data.filter(item =>
        item.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.bonusType?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        const pages = [];
        let startPage = Math.max(1, currentPage - 2);
        let endPage = Math.min(totalPages, startPage + 4);
        if (endPage === totalPages) startPage = Math.max(1, totalPages - 4);

        for (let i = Math.max(1, startPage); i <= endPage; i++) {
            pages.push(
                <button key={i} onClick={() => paginate(i)} className={`page-number ${currentPage === i ? 'active' : ''}`}>
                    {i}
                </button>
            );
        }
        return pages;
    };

    return (
        <div className="data-page-container">
            <header className="header-card">
                <div className="header-info">
                    <h2>{title} <span className="count-badge">{filteredData.length}</span></h2>
                    <p>Yönetim Paneli - Canlı Kontrol</p>
                </div>

                <div className="action-group">
                    {/* --- DİNAMİK SİSTEM GÖSTERGESİ --- */}
                    <div className={`system-indicator ${isSystemActive ? 'active' : 'passive'}`}>
                        <div className={`status-dot ${isSystemActive ? 'pulse' : ''}`}></div>
                        {loadingStatus ? '...' : (isSystemActive ? 'Sistem Aktif' : 'Sistem Kapalı')}
                    </div>

                    <button className={`btn-modern btn-filter ${filterVisible ? 'active' : ''}`} onClick={() => setFilterVisible(!filterVisible)}>
                        <FaFilter /> {filterVisible ? 'Kapat' : 'Filtrele'}
                    </button>
                    {!isPendingPage && (
                        <button className="btn-modern btn-refresh" onClick={fetchData}>
                            <FaSync /> Yenile
                        </button>
                    )}
                </div>
            </header>

            {filterVisible && (
                <div className="search-bar-container">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Kullanıcı veya bonus türü ara..."
                        value={searchTerm}
                        onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    />
                </div>
            )}

            <div className="table-container">
                <table className="modern-table">
                    <thead>
                        <tr>
                            <th><FaUserAlt className="th-icon" /> KULLANICI</th>
                            <th>BONUS TÜRÜ</th>
                            <th>DURUM</th>
                            <th>{(isCompletedPage || isPendingPage) ? "BİLGİ / NOT" : "RED NEDENİ"}</th>
                            <th>TARİH</th>
                            {isPendingPage && <th>BEKLEME</th>}
                            <th>ATANAN</th>
                            {isPendingPage && <th style={{ textAlign: 'center' }}>İŞLEMLER</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.length > 0 ? (
                            currentItems.map((item) => (
                                <tr key={item._id} className="table-row">
                                    <td className="user-name">{item.username}</td>
                                    <td>
                                        {bonusNames[item.bonusType] ? (
                                            <strong style={{ color: '#fff' }}>{bonusNames[item.bonusType]}</strong>
                                        ) : (
                                            <span style={{ color: '#64748b', fontSize: '13px' }}>
                                                {item.bonusType} (Bilinmeyen)
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <span className={`badge badge-${item.status}`}>
                                            {item.status === 'pending' ? 'Bekliyor' : item.status === 'approved' ? 'Onaylandı' : 'Reddedildi'}
                                        </span>
                                    </td>
                                    {/* Red Sebebi Yazdırma ve Kopyalama Alanı */}
                                    <td className="reason-cell">
                                        <div
                                            className="reason-badge"
                                            title="Kopyalamak için tıkla"
                                            onClick={() => copyToClipboard(item.rejectReason || '—')}
                                        >
                                            {item.rejectReason || '—'}
                                        </div>
                                    </td>
                                    <td className="date-cell">{dayjs(item.createdAt).format('DD MMM, HH:mm')}</td>
                                    {isPendingPage && <td className="wait-time">{dayjs(item.createdAt).fromNow(true)}</td>}
                                    <td className="admin-name">{item.assignedTo || '—'}</td>
                                    {isPendingPage && (
                                        <td className="action-cells">
                                            <button className="btn-action approve" onClick={() => handleApprove(item.username)}>
                                                <FaCheck />
                                            </button>
                                            <button className="btn-action reject" onClick={() => openRejectModal(item.username)}>
                                                <FaTimes />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr><td colSpan="8" className="empty-state">Veri bulunamadı.</td></tr>
                        )}
                    </tbody>
                </table>

                <footer className="pagination-bar">
                    <div className="pagination-info">
                        Toplam <b>{filteredData.length}</b> kayıttan <b>{indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredData.length)}</b> arası gösteriliyor
                    </div>
                    <div className="pagination-controls">
                        <button disabled={currentPage === 1} onClick={() => paginate(currentPage - 1)} className="page-nav-btn"><FaChevronLeft /></button>
                        {renderPagination()}
                        <button disabled={currentPage === totalPages} onClick={() => paginate(currentPage + 1)} className="page-nav-btn"><FaChevronRight /></button>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default DataTable;